import React from "react";
import { X } from "@phosphor-icons/react";

/**
 * Chip-style multi-value input.
 * - Type value, press Enter or comma to add
 * - Click × to remove
 * - Backspace on empty removes last chip
 * - Suggestions close on outside click (won't overlay siblings)
 */
export function ChipInput({ values = [], onChange, placeholder = "Type and press Enter…", testid, suggestions = [] }) {
  const [draft, setDraft] = React.useState("");
  const [open, setOpen] = React.useState(false);
  const rootRef = React.useRef(null);

  // Close suggestions on any outside mousedown
  React.useEffect(() => {
    function onDocMouseDown(e) {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, []);

  const add = (val) => {
    const v = val.trim();
    if (!v) return;
    if (values.some((x) => x.toLowerCase() === v.toLowerCase())) {
      setDraft("");
      return;
    }
    onChange([...values, v]);
    setDraft("");
  };

  const remove = (idx) => onChange(values.filter((_, i) => i !== idx));

  const onKey = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      add(draft);
    } else if (e.key === "Backspace" && !draft && values.length) {
      remove(values.length - 1);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const filtered = suggestions
    .filter((s) => !values.some((v) => v.toLowerCase() === s.toLowerCase()))
    .filter((s) => (draft ? s.toLowerCase().includes(draft.toLowerCase()) : true))
    .slice(0, 6);

  return (
    <div ref={rootRef} className="relative">
      <div
        className={`min-h-[44px] w-full flex flex-wrap gap-1.5 items-center rounded-md border bg-white px-2 py-1.5 transition-colors ${
          open ? "border-brand ring-2 ring-brand/20" : "border-zinc-300"
        }`}
        onClick={(e) => e.currentTarget.querySelector("input")?.focus()}
      >
        {values.map((v, i) => (
          <span key={`${v}-${i}`} className="city-chip" data-testid={`${testid}-chip-${i}`}>
            {v}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); remove(i); }}
              className="hover:text-brand-hover"
              aria-label={`Remove ${v}`}
              data-testid={`${testid}-remove-${i}`}
            >
              <X size={11} weight="bold" />
            </button>
          </span>
        ))}
        <input
          value={draft}
          onChange={(e) => { setDraft(e.target.value); setOpen(true); }}
          onKeyDown={onKey}
          onFocus={() => setOpen(true)}
          placeholder={values.length === 0 ? placeholder : ""}
          data-testid={testid}
          className="flex-1 min-w-[120px] outline-none bg-transparent text-sm px-1 py-1 text-zinc-900 placeholder:text-zinc-400"
        />
      </div>
      {open && filtered.length > 0 && (
        <div className="absolute z-30 mt-1 left-0 right-0 bg-white border border-zinc-200 rounded-md shadow-lg max-h-52 overflow-auto">
          {filtered.map((s) => (
            <button
              type="button"
              key={s}
              onMouseDown={(e) => { e.preventDefault(); add(s); }}
              className="w-full text-left px-3 py-2 text-sm text-zinc-700 hover:bg-brand-soft hover:text-brand transition-colors"
              data-testid={`${testid}-suggestion-${s}`}
            >
              {s}
            </button>
          ))}
        </div>
      )}
      <p className="text-[11px] text-zinc-400 mt-1">Press <span className="font-mono-num">Enter</span> or comma to add · Backspace to remove</p>
    </div>
  );
}
